import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface Webinar {
    id: string;
    title: string;
    description: string;
    learning_objectives: string;
    webinar_type: string;
    status: string;
    presenter_name: string;
    presenter_bio: string;
    presenter_title: string;
    presenter_organization: string;
    scheduled_date: string;
    duration_minutes: number;
    timezone: string;
    is_free: boolean;
    registration_fee: number;
    max_participants: number;
    registration_deadline: string;
    requires_approval: boolean;
    meeting_url: string;
    recording_url: string;
    offers_credits: boolean;
    credit_hours: number;
    accreditation_body: string;
    registration_count: number;
    attendance_count: number;
    completion_count: number;
    rating_sum: number;
    rating_count: number;
    created_by_name: string;
    created_at: string;
}

interface Props {
    onBack: () => void;
}

const WebinarsCenter: React.FC<Props> = ({ onBack }) => {
    const baseUrl = useBaseUrl();
    const [webinars, setWebinars] = useState<Webinar[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(12);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);

    const webinarTypes = [
        { value: 'live', label: 'Live Webinar', color: '#dc3545', icon: 'bx-broadcast' },
        { value: 'recorded', label: 'Recorded Session', color: '#6610f2', icon: 'bx-play-circle' },
        { value: 'series', label: 'Webinar Series', color: '#fd7e14', icon: 'bx-collection' },
        { value: 'workshop', label: 'Interactive Workshop', color: '#28a745', icon: 'bx-group' },
        { value: 'conference', label: 'Conference Session', color: '#007bff', icon: 'bx-buildings' }
    ];

    const statusOptions = [
        { value: 'planned', label: 'Planned', color: '#6c757d' },
        { value: 'scheduled', label: 'Scheduled', color: '#007bff' },
        { value: 'live', label: 'Live Now', color: '#dc3545' },
        { value: 'completed', label: 'Completed', color: '#28a745' },
        { value: 'cancelled', label: 'Cancelled', color: '#fd7e14' },
        { value: 'archived', label: 'Archived', color: '#6c757d' }
    ];

    useEffect(() => {
        loadWebinars();
    }, [selectedType, selectedStatus]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || loading || !hasMore) {
                return;
            }
            loadMoreWebinars();
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, hasMore]);

    const loadWebinars = async () => {
        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/webinars/`;
            const params = new URLSearchParams();
            
            if (selectedType !== 'all') params.append('webinar_type', selectedType);
            if (selectedStatus !== 'all') params.append('status', selectedStatus);
            params.append('page_size', itemsPerPage.toString());

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                if (Array.isArray(response.data.results)) {
                    setWebinars(response.data.results);
                    setTotalCount(response.data.count || 0);
                    setHasMore(!!response.data.next);
                } else if (Array.isArray(response.data)) {
                    setWebinars(response.data);
                    setTotalCount(response.data.length);
                    setHasMore(false);
                }
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error loading webinars:', error);
            setWebinars([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreWebinars = async () => {
        if (loading || !hasMore) return;

        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/webinars/`;
            const params = new URLSearchParams();
            
            if (selectedType !== 'all') params.append('webinar_type', selectedType);
            if (selectedStatus !== 'all') params.append('status', selectedStatus);
            params.append('page_size', itemsPerPage.toString());
            params.append('page', (currentPage + 1).toString());

            url += `?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && Array.isArray(response.data.results)) {
                setWebinars(prev => [...prev, ...response.data.results]);
                setHasMore(!!response.data.next);
                setCurrentPage(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error loading more webinars:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTypeInfo = (type: string) => {
        return webinarTypes.find(t => t.value === type) || { label: type, color: '#6c757d', icon: 'bx-video' };
    };

    const getStatusInfo = (status: string) => {
        return statusOptions.find(s => s.value === status) || { label: status, color: '#6c757d' };
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
        }
        return `${mins}m`;
    };

    const calculateAverageRating = (webinar: Webinar) => {
        if (webinar.rating_count > 0) {
            return (webinar.rating_sum / webinar.rating_count).toFixed(1);
        }
        return '0.0';
    };

    const isRegistrationOpen = (webinar: Webinar) => {
        const now = new Date();
        const deadline = new Date(webinar.registration_deadline);
        const scheduled = new Date(webinar.scheduled_date);
        
        return now < deadline && now < scheduled && webinar.status === 'scheduled';
    };

    const isUpcoming = (webinar: Webinar) => {
        const now = new Date();
        const scheduled = new Date(webinar.scheduled_date);
        return scheduled > now;
    };

    const viewWebinar = (webinar: Webinar) => {
        const typeInfo = getTypeInfo(webinar.webinar_type);
        const statusInfo = getStatusInfo(webinar.status);
        const avgRating = calculateAverageRating(webinar);
        
        Swal.fire({
            title: webinar.title,
            html: `
                <div class="text-start">
                    <div class="d-flex justify-content-between mb-3">
                        <span class="badge" style="background-color: ${typeInfo.color}">
                            <i class="${typeInfo.icon} me-1"></i>
                            ${typeInfo.label}
                        </span>
                        <span class="badge" style="background-color: ${statusInfo.color}">
                            ${statusInfo.label}
                        </span>
                    </div>
                    
                    <p><strong>Presenter:</strong> ${webinar.presenter_title} ${webinar.presenter_name}</p>
                    ${webinar.presenter_organization ? `<p><strong>Organization:</strong> ${webinar.presenter_organization}</p>` : ''}
                    <p><strong>Scheduled:</strong> ${formatDate(webinar.scheduled_date)}</p>
                    <p><strong>Duration:</strong> ${formatDuration(webinar.duration_minutes)}</p>
                    <p><strong>Cost:</strong> ${webinar.is_free ? 'Free' : `$${webinar.registration_fee}`}</p>
                    <p><strong>Max Participants:</strong> ${webinar.max_participants || 'Unlimited'}</p>
                    <p><strong>Registration Deadline:</strong> ${formatDate(webinar.registration_deadline)}</p>
                    
                    ${webinar.offers_credits ? `
                        <p><strong>CE/CME Credits:</strong> ${webinar.credit_hours} hours</p>
                        <p><strong>Accredited by:</strong> ${webinar.accreditation_body}</p>
                    ` : ''}
                    
                    <div class="mb-3">
                        <strong>Registration:</strong> ${webinar.registration_count} registered
                        ${webinar.attendance_count > 0 ? ` | ${webinar.attendance_count} attended` : ''}
                        ${webinar.completion_count > 0 ? ` | ${webinar.completion_count} completed` : ''}
                    </div>
                    
                    ${webinar.rating_count > 0 ? `
                        <div class="mb-3">
                            <strong>Rating:</strong> ${avgRating}/5.0 (${webinar.rating_count} reviews)
                        </div>
                    ` : ''}
                    
                    <hr>
                    <div class="mb-3">
                        <strong>Description:</strong>
                        <div>${webinar.description}</div>
                    </div>
                    
                    ${webinar.learning_objectives ? `
                        <div class="mb-3">
                            <strong>Learning Objectives:</strong>
                            <div>${webinar.learning_objectives}</div>
                        </div>
                    ` : ''}
                    
                    ${webinar.presenter_bio ? `
                        <div>
                            <strong>About the Presenter:</strong>
                            <div>${webinar.presenter_bio}</div>
                        </div>
                    ` : ''}
                </div>
            `,
            width: '700px',
            showCloseButton: true,
            showCancelButton: isRegistrationOpen(webinar),
            confirmButtonText: webinar.status === 'live' && webinar.meeting_url ? 'Join Live' : 
                             webinar.recording_url ? 'Watch Recording' : 'Close',
            cancelButtonText: isRegistrationOpen(webinar) ? 'Register' : undefined,
            customClass: {
                htmlContainer: 'text-start'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                if (webinar.status === 'live' && webinar.meeting_url) {
                    window.open(webinar.meeting_url, '_blank');
                } else if (webinar.recording_url) {
                    window.open(webinar.recording_url, '_blank');
                }
            } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
                // Handle registration
                handleRegistration(webinar);
            }
        });
    };

    const handleRegistration = (webinar: Webinar) => {
        Swal.fire({
            title: 'Register for Webinar',
            text: `Would you like to register for "${webinar.title}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Register',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                // Here you would typically make an API call to register
                Swal.fire('Success!', 'You have been registered for this webinar.', 'success');
            }
        });
    };

    const filteredWebinars = webinars.filter(webinar =>
        webinar.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        webinar.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        webinar.presenter_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="container-fluid p-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">
                                <i className="bx bx-video me-2 text-primary"></i>
                                Training & Webinars
                            </h4>
                            <p className="text-muted mb-0">Educational webinars and training sessions</p>
                        </div>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={onBack}
                        >
                            <i className="bx bx-arrow-back me-2"></i>
                            Back to Resources
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                    <div className="position-relative">
                        <i className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <input
                            type="text"
                            className="form-control ps-5"
                            placeholder="Search webinars..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-lg-4 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                    >
                        <option value="all">All Types</option>
                        {webinarTypes.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-lg-4 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        {statusOptions.map(status => (
                            <option key={status.value} value={status.value}>
                                {status.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Results Summary */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">
                            Showing {filteredWebinars.length} of {totalCount} webinars
                        </span>
                    </div>
                </div>
            </div>

            {/* Webinars Grid */}
            <div className="row">
                {filteredWebinars.map(webinar => {
                    const typeInfo = getTypeInfo(webinar.webinar_type);
                    const statusInfo = getStatusInfo(webinar.status);
                    const avgRating = calculateAverageRating(webinar);
                    const registrationOpen = isRegistrationOpen(webinar);
                    const upcoming = isUpcoming(webinar);

                    return (
                        <div key={webinar.id} className="col-lg-4 col-md-6 mb-4">
                            <div className={`card h-100 ${webinar.status === 'live' ? 'border-danger' : ''}`}>
                                {webinar.status === 'live' && (
                                    <div className="card-header bg-danger text-white py-1">
                                        <small>
                                            <i className="bx bx-broadcast me-1"></i>
                                            Live Now
                                        </small>
                                    </div>
                                )}
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span 
                                            className="badge" 
                                            style={{ backgroundColor: typeInfo.color }}
                                        >
                                            <i className={`${typeInfo.icon} me-1`}></i>
                                            {typeInfo.label}
                                        </span>
                                        <span 
                                            className="badge" 
                                            style={{ backgroundColor: statusInfo.color }}
                                        >
                                            {statusInfo.label}
                                        </span>
                                    </div>
                                    
                                    <h6 className="card-title mb-2">{webinar.title}</h6>
                                    <p className="card-text text-muted small mb-3">
                                        {webinar.description.substring(0, 120)}...
                                    </p>
                                    
                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            <i className="bx bx-user me-1"></i>
                                            {webinar.presenter_title} {webinar.presenter_name}
                                        </small>
                                        <small className="text-muted d-block">
                                            <i className="bx bx-calendar me-1"></i>
                                            {formatDate(webinar.scheduled_date)}
                                        </small>
                                        <small className="text-muted d-block">
                                            <i className="bx bx-time me-1"></i>
                                            Duration: {formatDuration(webinar.duration_minutes)}
                                        </small>
                                        <small className="text-muted d-block">
                                            <i className="bx bx-dollar me-1"></i>
                                            {webinar.is_free ? 'Free' : `$${webinar.registration_fee}`}
                                        </small>
                                    </div>
                                    
                                    {webinar.offers_credits && (
                                        <div className="mb-2">
                                            <small className="text-info">
                                                <i className="bx bx-certification me-1"></i>
                                                {webinar.credit_hours} CE/CME Credits
                                            </small>
                                        </div>
                                    )}
                                    
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="text-muted">
                                            <i className="bx bx-group me-1"></i>
                                            {webinar.registration_count} registered
                                        </small>
                                        {webinar.rating_count > 0 && (
                                            <small className="text-muted">
                                                <i className="bx bx-star me-1"></i>
                                                {avgRating}/5.0 ({webinar.rating_count})
                                            </small>
                                        )}
                                    </div>
                                    
                                    <div className="d-flex gap-2">
                                        <button 
                                            className="btn btn-outline-primary btn-sm flex-grow-1"
                                            onClick={() => viewWebinar(webinar)}
                                        >
                                            <i className="bx bx-info-circle me-1"></i>
                                            Details
                                        </button>
                                        
                                        {webinar.status === 'live' && webinar.meeting_url && (
                                            <button 
                                                className="btn btn-danger btn-sm"
                                                onClick={() => window.open(webinar.meeting_url, '_blank')}
                                            >
                                                <i className="bx bx-broadcast me-1"></i>
                                                Join
                                            </button>
                                        )}
                                        
                                        {registrationOpen && (
                                            <button 
                                                className="btn btn-success btn-sm"
                                                onClick={() => handleRegistration(webinar)}
                                            >
                                                <i className="bx bx-user-plus me-1"></i>
                                                Register
                                            </button>
                                        )}
                                        
                                        {webinar.recording_url && webinar.status === 'completed' && (
                                            <button 
                                                className="btn btn-info btn-sm"
                                                onClick={() => window.open(webinar.recording_url, '_blank')}
                                            >
                                                <i className="bx bx-play me-1"></i>
                                                Watch
                                            </button>
                                        )}
                                    </div>
                                    
                                    {registrationOpen && (
                                        <div className="mt-2">
                                            <small className="text-success">
                                                <i className="bx bx-check-circle me-1"></i>
                                                Registration Open
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Loading State */}
            {loading && (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">Loading webinars...</div>
                </div>
            )}

            {/* No Results */}
            {!loading && filteredWebinars.length === 0 && (
                <div className="text-center py-5">
                    <i className="bx bx-video display-4 text-muted"></i>
                    <h5 className="mt-3 text-muted">No webinars found</h5>
                    <p className="text-muted">Try adjusting your filters or search terms.</p>
                </div>
            )}

            {/* Infinite Scroll Indicator */}
            {!loading && hasMore && filteredWebinars.length > 0 && (
                <div className="text-center py-3">
                    <small className="text-muted">Scroll down to load more webinars</small>
                </div>
            )}

            <style>{`
                .card {
                    transition: transform 0.2s, box-shadow 0.2s;
                    border: 1px solid #e3e6f0;
                }
                
                .card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.15);
                }
                
                .badge {
                    font-size: 0.75rem;
                }
                
                .card-title {
                    font-size: 1rem;
                    font-weight: 600;
                    line-height: 1.3;
                }
                
                .card-text {
                    font-size: 0.875rem;
                    line-height: 1.4;
                }
            `}</style>
        </div>
    );
};

export default WebinarsCenter;
